import { CartUpdateEvent } from '@theme/events';

/**
 * One-tap tier swap for a cart line: removes the current line and adds the next
 * (more expensive) variant of the same product, so the cart total moves to the
 * higher tier's price instead of adding a second line.
 *
 * The final `/cart/change` call requests the cart sections back; dispatching a
 * CartUpdateEvent with those sections lets every cart-items-component morph
 * itself through the theme's normal cart-update path (drawer + /cart page).
 */
class CartUpgradeComponent extends HTMLElement {
  #busy = false;

  connectedCallback() {
    // Delegate on the component itself (not the button). The cart-update morph
    // reuses this <cart-upgrade-component> element and swaps its inner button
    // node for the next tier — a listener bound to the old button would be lost,
    // so every upgrade after the first was a dead click. Delegation survives that.
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target ? target.closest('[data-upgrade-button]') : null;
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    if (this.#busy) return;

    const lineKey = this.dataset.lineKey;
    const newVariantId = Number(this.dataset.newVariantId);
    if (!lineKey || !Number.isFinite(newVariantId) || newVariantId <= 0) return;
    // Vor dem Morph festhalten: danach zeigt dieselbe Karte schon die nächste Stufe.
    const messung = {
      produkt: this.dataset.productId || '',
      von: this.dataset.currentVariantId || '',
      nach: this.dataset.newVariantId || '',
      nachPreis: this.dataset.newPrice || '',
    };

    /** @type {Record<string, string>} */
    let properties = {};
    try {
      const parsed = JSON.parse(this.dataset.properties || '{}');
      // Liquid gibt `item.properties | json` als Liste von Paaren aus ([["Hinweis","…"]]).
      // /cart/add verlangt ein Objekt – ein Array führte zu 400 und einem toten Upgrade-Knopf (16.10.).
      if (Array.isArray(parsed)) {
        for (const pair of parsed) {
          if (Array.isArray(pair) && pair.length === 2 && pair[1] != null && pair[1] !== '') properties[String(pair[0])] = String(pair[1]);
        }
      } else if (parsed && typeof parsed === 'object') {
        properties = parsed;
      }
    } catch {
      properties = {};
    }

    const sectionIds = [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
          .filter(/** @returns {value is string} */ (value) => Boolean(value))
      ),
    ];

    this.#setBusy(true);
    const alterFehler = this.querySelector('[data-upgrade-fehler]');
    if (alterFehler instanceof HTMLElement) alterFehler.hidden = true;

    try {
      // 1) Add the higher tier first so the cart is never momentarily empty.
      // When the fixed-surcharge mode is active, tag the new line with the two
      // markers the "Upgrade-Aktion" discount function reads to reprice it to
      // (already-paid base price + fixed surcharge). Hidden ("_"-prefixed) line
      // properties — inert and invisible until the discount function is live.
      const fixedMode = this.dataset.upgradeFixed === 'true';
      const fromPrice = Number(this.dataset.fromPrice);
      const surcharge = Number(this.dataset.surcharge);
      if (fixedMode && Number.isFinite(fromPrice) && fromPrice > 0) {
        properties['_upgrade_from_price'] = String(fromPrice);
        if (Number.isFinite(surcharge) && surcharge >= 0) {
          properties['_upgrade_surcharge'] = String(surcharge);
        }
      }

      // Messung Warenkorb-Upsell: versteckte Markierung an der neuen Zeile, landet in der Bestellung
      // (Plattform: lib/shop-upsell.ts, /admin/analytics/upsells). Bei mehreren Upgrades hintereinander
      // bleibt die erste Ausgangsstufe stehen, weil die Eigenschaften der alten Zeile oben übernommen werden.
      // _th_upsell_kette hält jeden Stufenwechsel mit Preisen fest (`von@Cent~nach@Cent`, mit Komma getrennt),
      // damit der Bericht einen Kauf über zwei Stufen auf beide Wechsel buchen kann. Kein „>“: Shopify gibt
      // Eigenschaften im Liquid HTML-maskiert aus, beim nächsten Upgrade stünde sonst „&gt;“ in der Kette.
      // eBook-Tausch (34d) bekommt keinen Upgrade-Vorteil: die Rabatt-Function der App th-warenkorb-rabatt
      // überspringt Zeilen mit _th_upsell_art=buch. War die Zeile schon ein Paket-Upgrade, bleibt der Vorteil.
      const warUpgrade = properties['_th_upsell'] === 'upgrade';
      if (this.dataset.art === 'buch' && !warUpgrade) properties['_th_upsell_art'] = 'buch';
      properties['_th_upsell'] = 'upgrade';
      if (!properties['_th_upsell_von'] && messung.von) {
        properties['_th_upsell_von'] = messung.von;
        if (Number.isFinite(fromPrice) && fromPrice >= 0) properties['_th_upsell_von_preis'] = String(fromPrice);
      }
      if (messung.von && messung.nach) {
        const preis = (/** @type {unknown} */ wert) => (/^[0-9]+$/.test(String(wert)) ? `@${wert}` : '');
        const glied = `${messung.von}${preis(fromPrice)}~${messung.nach}${preis(messung.nachPreis)}`;
        properties['_th_upsell_kette'] = properties['_th_upsell_kette'] ? `${properties['_th_upsell_kette']},${glied}` : glied;
      }

      const addBody = { id: newVariantId, quantity: 1 };
      if (Object.keys(properties).length > 0) addBody.properties = properties;

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addBody),
      });
      if (!addResponse.ok) throw new Error(`add failed: ${addResponse.status}`);

      // 2) Remove the current line by its key, requesting rendered sections back.
      const changeResponse = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!changeResponse.ok) throw new Error(`change failed: ${changeResponse.status}`);

      const cart = await changeResponse.json();

      this.#announce('Paket aktualisiert.');

      // 3) Let the theme's cart-update path morph the cart section(s).
      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id || 'cart-upgrade', {
          source: 'cart-upgrade',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
      // Erst nach dem Morph und ohne Einfluss auf den Tausch (upsellMelden wirft nie).
      upsellMelden('upgrade', 'angenommen', messung.produkt, messung.von, messung.nach);
      // Fokus nicht auf <body> fallen lassen (WCAG 2.4.3, Opus-Review 27.10.2026): nächste Karte, sonst Kasse.
      // Nach dem Morph kann diese Karte ersetzt oder weg sein (höchste Stufe, /cart): der Reihe nach ausweichen.
      setTimeout(() => requestAnimationFrame(() => {
        const ziel = [
          this.isConnected && this.querySelector('[data-upgrade-button]'),
          document.querySelector(`cart-upgrade-component[data-product-id="${messung.produkt}"] [data-upgrade-button]`),
          document.querySelector('dialog[open] .cart-bump__add') || document.querySelector('.cart-page .cart-bump__add'),
          document.querySelector('dialog[open] .th-korb-fuss__kasse') || document.querySelector('.cart__checkout-button'),
        ].find((e) => e instanceof HTMLElement && e.isConnected && !(e instanceof HTMLButtonElement && e.disabled));
        if (ziel instanceof HTMLElement) ziel.focus({ preventScroll: true });
      }), 0);
    } catch (error) {
      console.error('[cart-upgrade]', error);
      this.#announce('Upgrade fehlgeschlagen. Bitte erneut versuchen.');
      const fehler = this.querySelector('[data-upgrade-fehler]');
      if (fehler instanceof HTMLElement) fehler.hidden = false;
    } finally {
      // The morph reuses this element (it is not removed), so always clear the
      // busy state and re-enable the current button so the next tier is clickable.
      this.#setBusy(false);
    }
  };

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('upgrading', busy);
    // Während des Tauschs liegen kurz beide Pakete im Korb: Kasse so lange sperren.
    document.querySelectorAll('.th-korb-fuss__kasse, .cart__checkout-button').forEach((kasse) => {
      if (kasse instanceof HTMLButtonElement) kasse.toggleAttribute('data-th-tausch', busy);
      if (kasse instanceof HTMLButtonElement && !kasse.hasAttribute('data-th-consent-kasse')) kasse.disabled = busy;
    });
    const button = this.querySelector('[data-upgrade-button]');
    if (!button) return;
    button.disabled = busy;
    const label = button.querySelector('.cart-upgrade__btn-label');
    if (label) label.textContent = busy ? 'Wird hinzugefügt …' : 'Hinzufügen';
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #announce(message) {
    const region = this.querySelector('[ref="upgradeStatus"]');
    if (!(region instanceof HTMLElement)) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }
}

if (!customElements.get('cart-upgrade-component')) {
  customElements.define('cart-upgrade-component', CartUpgradeComponent);
}

/* ---------- Messung Warenkorb-Upsells (Philipp, 20.10.2026) ----------
 * Zählt „gezeigt“ (Karte mindestens zur Hälfte im Bild) und „angenommen“ für das Upgrade
 * (snippets/cart-upgrade-offer.liquid) und die Premium-Karte (snippets/cart-premium-bump.liquid).
 * Je Meldung ein Aufruf an die Plattform (ohne Cookies), die nur Summen je Tag, Angebot, Produkt und
 * Stufenwechsel speichert (ohne IP, Cookie, Uhrzeit). Im Browser wird nichts gespeichert, deshalb ohne
 * Einwilligung, gleiches Muster wie die Partnerlink-Zählung (snippets/th-partner.liquid). Gesendet wird
 * nur vom veröffentlichten Theme auf testhelden.com, nicht aus Vorschau oder Theme-Editor.
 * Bericht: Plattform /admin/analytics/upsells.
 */
const UPSELL_ZAEHLER = 'https://app.testhelden.com/api/mobile/shop/upsell';
const LIVE_SHOP = /^(www\.)?testhelden\.com$/;
/** @type {Set<string>} */
const schonGezeigt = new Set();

function istLiveTheme() {
  const shopify = /** @type {any} */ (window).Shopify;
  return LIVE_SHOP.test(location.hostname) && shopify?.theme?.role === 'main' && !shopify?.designMode;
}

/**
 * Meldet ein Ereignis; wirft nie (darf den Warenkorb nicht stören).
 * @param {string} art - upgrade | bump
 * @param {string} ereignis - gezeigt | angenommen
 * @param {string | undefined} produkt
 * @param {string} von - Varianten-ID vor dem Upgrade, leer beim Bump
 * @param {string | undefined} nach
 */
function upsellMelden(art, ereignis, produkt, von, nach) {
  try {
    if (!produkt || !nach || (art === 'upgrade' && (!von || von === nach))) return;
    if (ereignis === 'gezeigt') {
      // Einmal je Seitenaufruf und Angebot; die Plattform drosselt zusätzlich je Netz (30 Minuten).
      const schluessel = `${art}|${produkt}|${von}>${nach}`;
      if (schonGezeigt.has(schluessel)) return;
      schonGezeigt.add(schluessel);
    }
    if (!istLiveTheme()) return;
    // keepalive: kommt auch an, wenn direkt danach die Seite wechselt (z. B. „Zur Kasse“).
    // credentials 'omit': kein Anmelde-Cookie der Lernplattform geht mit.
    fetch(UPSELL_ZAEHLER, {
      method: 'POST',
      body: JSON.stringify({ art, ereignis, produkt, von, nach }),
      keepalive: true,
      credentials: 'omit',
      mode: 'no-cors',
    }).catch(() => {});
  } catch {}
}

/**
 * Unsichtbarer Messpunkt in einer Angebotskarte: beobachtet die Karte (Elternelement) und meldet
 * „gezeigt“, sobald sie zur Hälfte im Bild ist. Ein geschlossener Warenkorb (dialog) zählt nicht.
 * Beim Premium-Bump meldet er auch „angenommen“, wenn das Formular der Karte erfolgreich hinzufügt.
 */
class UpsellSicht extends HTMLElement {
  static observedAttributes = ['data-von', 'data-nach'];

  /** @type {IntersectionObserver | null} */
  #beobachter = null;
  /** @type {HTMLElement | null} */
  #karte = null;
  #geplant = false;

  connectedCallback() {
    this.#karte = this.parentElement;
    if (!this.#karte) return;
    if ('IntersectionObserver' in window) {
      this.#beobachter = new IntersectionObserver(
        (eintraege) => {
          for (const e of eintraege) {
            if (e.isIntersecting && e.intersectionRatio >= 0.5) this.#melden('gezeigt');
          }
        },
        { threshold: [0, 0.5] }
      );
      this.#beobachter.observe(this.#karte);
    }
    if (this.dataset.art === 'bump') this.#karte.addEventListener('cart:update', this.#bumpAngenommen);
  }

  disconnectedCallback() {
    this.#beobachter?.disconnect();
    this.#beobachter = null;
    this.#karte?.removeEventListener('cart:update', this.#bumpAngenommen);
    this.#karte = null;
  }

  /**
   * Der Warenkorb-Morph behält die Karte und tauscht nur die Stufe (39 → 69, danach 69 → 119).
   * Danach wird die Sichtbarkeit frisch gemessen: neu beobachten löst sofort eine Meldung mit dem
   * aktuellen Stand aus. Der Morph setzt data-von und data-nach nacheinander, deshalb erst im
   * Microtask danach (sonst ginge der Zwischenstand „69 → 69“ raus).
   * @param {string} _name
   * @param {string | null} alt
   * @param {string | null} neu
   */
  attributeChangedCallback(_name, alt, neu) {
    if (alt === neu || !this.#beobachter || !this.#karte || this.#geplant) return;
    this.#geplant = true;
    queueMicrotask(() => {
      this.#geplant = false;
      if (!this.#beobachter || !this.#karte) return;
      this.#beobachter.unobserve(this.#karte);
      this.#beobachter.observe(this.#karte);
    });
  }

  /** @param {Event} event */
  #bumpAngenommen = (event) => {
    const daten = /** @type {any} */ (event).detail?.data;
    if (daten?.didError) return;
    this.#melden('angenommen');
  };

  /** @param {string} ereignis */
  #melden(ereignis) {
    upsellMelden(this.dataset.art || '', ereignis, this.dataset.produkt, this.dataset.von || '', this.dataset.nach);
  }
}

// Mülleimer an einer hochgestuften Zeile (assets/component-cart-items.js, Philipp 28.10.2026).
document.addEventListener('th:upgrade-zurueck', (event) => {
  const d = /** @type {CustomEvent} */ (event).detail || {};
  upsellMelden('upgrade', 'zurueckgenommen', d.produkt, d.von || '', d.nach);
});

if (!customElements.get('th-upsell-sicht')) {
  customElements.define('th-upsell-sicht', UpsellSicht);
}
